import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  CustomerPlanningBoardService,
  FilterListsResponse,
  FilterListsResponse2,
  PaginatedApiResponseWithFilters,
  ProfileResponse,
  ProfileResponse2,
  PspPlanningBoardService,
} from '../../../api';
import { BaseRolesService } from '../../core/services/base-roles.service';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { PspPlanningBoardOrderKey } from '../../modules/planning-board/_types/base-planning-board-sort.type';
import { BaseCoreState } from '../reducers';
import { basePlanningBoardActions } from '../actions';
import { baseAuthSelectors } from '../selectors';

type PlanningBoardListResponse = PaginatedApiResponseWithFilters & {
  data?: Array<ProfileResponse> | Array<ProfileResponse2>;
  filters?: FilterListsResponse | FilterListsResponse2;
};

@Injectable()
export class BasePlanningBoardEffects {
  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private customerPlanningBoardService: CustomerPlanningBoardService,
    private pspPlanningBoardService: PspPlanningBoardService,
    private roleService: BaseRolesService
  ) {}

  loadPlanningBoardList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basePlanningBoardActions.loadPlanningBoardList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$: Observable<PlanningBoardListResponse> = this.roleService.isPsp()
          ? this.pspPlanningBoardService.getPspPlanningBoardList({
              ...action.payload,
              orderKey: action.payload.orderKey as PspPlanningBoardOrderKey | undefined,
              psp: user.organizationId,
            })
          : this.customerPlanningBoardService.getCustomerPlanningBoardList({
              ...action.payload,
              customer: user.organizationId,
            });

        return source$.pipe(
          mergeMap((response) => {
            return [
              basePlanningBoardActions.setPlanningBoardList({
                payload: response.data || [],
              }),
              basePlanningBoardActions.setPlanningBoardFiltersAndPagination({
                payload: { filters: response.filters, pagination: response.meta },
              }),
              basePlanningBoardActions.successLoadPlanningBoardList(),
            ];
          }),
          catchError(() => of(basePlanningBoardActions.errorLoadPlanningBoardList()))
        );
      })
    );
  });
}
